import { Fragment, useRef, type ReactNode } from "react";
import { usePageScript } from "../shop/page-script";

type Choice = {
  label: string;
  href?: string;
  value?: string;
  disabled?: boolean;
  external?: boolean;
  /** A quiet note at the item's end, such as how many posts a tag has. */
  detail?: string;
  /**
   * Makes the item, a link or a value, one of a set of choices, such as a filter's: true marks the
   * one shown now (aria-current and a check). Every choice keeps room for the check, so the menu's
   * width doesn't change with the current one.
   */
  current?: boolean;
  /** Keeps the reader's place when the link only changes the view (site/start/lib/navigation.ts). */
  preserveScroll?: boolean;
};

type Item = Choice | { separator: true } | { group: string };

/** The items in order, with a group label's items gathered under it until the next label or line. */
function sections(items: readonly Item[]) {
  const out: (
    | { separator: number }
    | { index: number; label?: string; items: [number, Choice][] }
  )[] = [];
  items.forEach((item, index) => {
    const last = out.at(-1);
    if ("separator" in item) out.push({ separator: index });
    else if ("group" in item) out.push({ index, label: item.group, items: [] });
    else if (last && "items" in last) last.items.push([index, item]);
    else out.push({ index, items: [[index, item]] });
  });
  return out;
}

function MenuItem({ item }: { item: Choice }) {
  const content = (
    <>
      {item.label}
      {item.detail && <span className="ui-menu-item-detail">{item.detail}</span>}
      {item.current !== undefined && (
        <span className="ui-menu-item-indicator" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m5 12 4.5 4.5L19 7"></path>
          </svg>
        </span>
      )}
    </>
  );
  return item.href && !item.disabled ? (
    <a
      className="ui-menu-item"
      role="menuitem"
      tabIndex={-1}
      href={item.href}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noopener" : undefined}
      aria-current={item.current ? "true" : undefined}
      data-preserve-scroll={item.preserveScroll ? "" : undefined}
    >
      {content}
    </a>
  ) : (
    <button
      type="button"
      className="ui-menu-item"
      role="menuitem"
      tabIndex={-1}
      value={item.value}
      aria-current={item.current ? "true" : undefined}
      aria-disabled={item.disabled ? "true" : undefined}
      data-disabled={item.disabled ? "" : undefined}
    >
      {content}
    </button>
  );
}

/**
 * A menu button: a list of links or actions under a trigger. Links
 * navigate; an item with a `value` fires `ui-menu-select` on the menu's root with `detail.value`.
 * A group label names the items that follow it, up to the next label or separator.
 * The kit's script (site/src/ui/menu.ts) places it and adds the keyboard. It reads its items once,
 * so a menu whose items change should be keyed by them.
 */
export function Menu({
  id,
  label,
  items,
  side = "bottom",
  align = "start",
  triggerClass = "ui-trigger",
  className,
  showChevron = true,
  ariaLabel,
}: {
  id: string;
  label: ReactNode;
  items: readonly Item[];
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  triggerClass?: string;
  className?: string;
  showChevron?: boolean;
  ariaLabel?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  usePageScript(root, async () => {
    const { mountMenu } = await import("../../../src/ui/menu");
    if (root.current) mountMenu(root.current);
  });
  return (
    <div
      ref={root}
      className={["ui-menu", className].filter(Boolean).join(" ")}
      data-ui="menu"
      data-side={side}
      data-align={align}
    >
      <button
        id={`${id}-trigger`}
        type="button"
        className={["ui-menu-trigger", triggerClass].filter(Boolean).join(" ")}
        popoverTarget={`${id}-popup`}
        aria-haspopup="menu"
        aria-expanded="false"
        aria-controls={`${id}-popup`}
        aria-label={ariaLabel}
      >
        {label}
        {showChevron && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6"></path>
          </svg>
        )}
      </button>
      <div
        id={`${id}-popup`}
        className="ui-menu-popup ui-floating"
        popover="auto"
        role="menu"
        aria-labelledby={`${id}-trigger`}
        tabIndex={-1}
      >
        {sections(items).map((section) =>
          "separator" in section ? (
            <div key={section.separator} className="ui-menu-separator" role="separator" />
          ) : section.label === undefined ? (
            <Fragment key={section.index}>
              {section.items.map(([index, item]) => (
                <MenuItem key={index} item={item} />
              ))}
            </Fragment>
          ) : (
            // The label names its group for screen readers, so it isn't read again as text.
            <div key={section.index} role="group" aria-labelledby={`${id}-group-${section.index}`}>
              <div
                id={`${id}-group-${section.index}`}
                className="ui-menu-group-label"
                aria-hidden="true"
              >
                {section.label}
              </div>
              {section.items.map(([index, item]) => (
                <MenuItem key={index} item={item} />
              ))}
            </div>
          ),
        )}
      </div>
    </div>
  );
}
